import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useAgentsList, AGENTS_DEFAULT_PAGE_SIZE } from "./useAgentsList.js";
import { useSuperAgentsList } from "./useSuperAgentsList.js";
import { getAgent, createAgent, updateAgent, listPlcs, createOrResetAgentLoginAccount, uploadAgentPhoto } from "./workerLineApi.js";
import { AgentDetailView, AgentCreateView, AgentEditView, AgentLoginCreatedView, AgentLoginAccountView, emptyAgentForm } from "./AgentViews.jsx";
import CopyInviteLinkButton from "../agent/CopyInviteLinkButton.jsx";
import { WORKER_LINE_STATUS_OPTIONS, statusBadgeVariant, describeListError, isJobDemandSide, agentLineNoun } from "./workerLineShared.js";
import "./workerLine.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import PageSkeleton from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "SuperAdmin", "Worker Line", "Agents"];
const DRAWER_TITLE_ID = "agent-drawer-title";
const SEARCH_DEBOUNCE_MS = 350;
const SUPER_AGENT_OPTIONS_PAGE_SIZE = 100;
const PLC_OPTIONS_PAGE_SIZE = 100;

/**
 * P161: what to tell an operator when the picture alone failed.
 *
 * Deliberately says "the Agent was saved" in every branch: that is the fact
 * that decides what they do next, and leaving it out would read as though the
 * whole record had been lost. 400 is the server's own validated refusal (wrong
 * type or too large) and carries its message through, since that message
 * already names which file to pick instead.
 */
function describeAgentPhotoError(error) {
  if (error?.status === 400 && error?.body?.message) {
    return `บันทึก Agent แล้ว แต่แนบรูปไม่สำเร็จ: ${error.body.message}`;
  }
  if (error?.status === 401) {
    return "บันทึก Agent แล้ว แต่เซสชันหมดอายุก่อนแนบรูป กรุณาเข้าสู่ระบบใหม่แล้วแนบรูปอีกครั้ง";
  }
  if (error?.status === 403) {
    return "บันทึก Agent แล้ว แต่คุณไม่มีสิทธิ์แนบรูปให้ Agent นี้";
  }
  if (error?.status === 413) {
    return "บันทึก Agent แล้ว แต่ไฟล์รูปใหญ่เกินไป กรุณาใช้รูปที่มีขนาดไม่เกิน 8 MB";
  }
  return "บันทึก Agent แล้ว แต่แนบรูปไม่สำเร็จ กรุณาลองแนบรูปอีกครั้งจากปุ่มแก้ไข Agent";
}

/** Fetches PLC options for one Super Agent id, refetching whenever the id changes; never fetches (and stays empty) while no Super Agent is selected - "no fake options" for a parent that has not been chosen yet. */
function usePlcOptionsForSuperAgent(superAgentOrganizationId) {
  const [state, setState] = useState({ status: "idle", items: [] });

  useEffect(() => {
    if (!superAgentOrganizationId) {
      setState({ status: "idle", items: [] });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", items: [] });
    listPlcs({ superAgentOrganizationId, page: 1, pageSize: PLC_OPTIONS_PAGE_SIZE })
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", items: data?.items ?? [] });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ status: "error", items: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [superAgentOrganizationId]);

  return state;
}

/**
 * /backoffice/worker-line/agents - Prompt 132 Worker Line Admin: Agents.
 * Entirely over the real Worker Line Admin API
 * (GET/POST/PATCH /api/backoffice/worker-line/agents[/{id}]). An Agent
 * always has a real parent Super Agent AND a real parent PLC - the Create
 * Agent drawer's PLC dropdown is sourced from GET .../worker-line/plcs
 * filtered by the chosen Super Agent, never a fake/static list, and is
 * disabled until a Super Agent is chosen.
 */
export default function AgentsListPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;
  // Mirrors InviteCardService.EnsureStandingInviteCardAsync's own rule
  // (SuperAdmin, or the Agent acting for itself). Rendering the button for a
  // Super Agent/PLC operator would only ever produce a 403 they cannot act on.
  const canIssueInviteLink = primaryRole === "SuperAdmin";
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [superAgentFilter, setSuperAgentFilter] = useState("");
  const [plcFilter, setPlcFilter] = useState("");
  const [page, setPage] = useState(1);

  const [selectedId, setSelectedId] = useState(null);
  const [drawerView, setDrawerView] = useState(null); // null | "detail" | "create" | "edit"
  const [detailState, setDetailState] = useState({ status: "idle", data: null, error: null, forId: null });
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [createForm, setCreateForm] = useState(emptyAgentForm());
  const [createValidationError, setCreateValidationError] = useState(null);
  const [createSubmit, setCreateSubmit] = useState({ status: "idle", error: null });
  // P161: the picture chosen in a form, held here until there is an Agent id to
  // attach it to. Kept out of createForm/editForm on purpose - those are JSON
  // request bodies, and a File has no business being spread into one.
  const [createPhotoFile, setCreatePhotoFile] = useState(null);
  const [editPhotoFile, setEditPhotoFile] = useState(null);
  // Reported separately from the create/edit submit error: the Agent itself
  // saved, only its picture did not.
  const [createPhotoError, setCreatePhotoError] = useState(null);
  const [editPhotoError, setEditPhotoError] = useState(null);
  // Prompt 136: set only when Create Agent's own createLoginAccount succeeded - the
  // credentials shown from this are the caller's own just-submitted form values,
  // never round-tripped through the server, and are discarded once the drawer closes.
  const [createdLoginInfo, setCreatedLoginInfo] = useState(null);

  const [editForm, setEditForm] = useState(null);
  const [editValidationError, setEditValidationError] = useState(null);
  const [editSubmit, setEditSubmit] = useState({ status: "idle", error: null });

  const [resetForm, setResetForm] = useState({ loginIdentifier: "", temporaryPassword: "" });
  const [resetValidationError, setResetValidationError] = useState(null);
  const [resetSubmit, setResetSubmit] = useState({ status: "idle", error: null });
  const [resetCreatedInfo, setResetCreatedInfo] = useState(null);

  const superAgentOptionsFilters = useMemo(() => ({ page: 1, pageSize: SUPER_AGENT_OPTIONS_PAGE_SIZE }), []);
  const { data: superAgentOptionsData } = useSuperAgentsList(superAgentOptionsFilters);
  const superAgentOptions = superAgentOptionsData?.items ?? [];

  const filterPlcOptions = usePlcOptionsForSuperAgent(superAgentFilter);
  const createPlcOptions = usePlcOptionsForSuperAgent(createForm.superAgentOrganizationId);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, superAgentFilter, plcFilter]);

  // Changing the Super Agent filter invalidates whatever PLC filter was
  // selected under the previous Super Agent - never leave a stale PLC id
  // filtering the list once its own parent filter no longer matches it.
  useEffect(() => {
    setPlcFilter("");
  }, [superAgentFilter]);

  const filters = useMemo(
    () => ({
      page,
      pageSize: AGENTS_DEFAULT_PAGE_SIZE,
      search,
      status: statusFilter,
      superAgentOrganizationId: superAgentFilter,
      plcOrganizationId: plcFilter,
    }),
    [page, search, statusFilter, superAgentFilter, plcFilter]
  );

  const { status: listStatus, data: listData, error: listError, refetch: refetchList } = useAgentsList(filters);

  useEffect(() => {
    if (!selectedId) {
      setDetailState({ status: "idle", data: null, error: null, forId: null });
      return undefined;
    }

    let cancelled = false;
    setDetailState((previous) => ({
      status: "loading",
      data: previous.forId === selectedId ? previous.data : null,
      error: null,
      forId: selectedId,
    }));

    getAgent(selectedId)
      .then((data) => {
        if (!cancelled) {
          setDetailState({ status: "ready", data, error: null, forId: selectedId });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailState({ status: "error", data: null, error, forId: selectedId });
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- detailReloadToken intentionally re-triggers a fetch for the same selectedId
  }, [selectedId, detailReloadToken]);

  function openDetail(id) {
    setSelectedId(id);
    setDrawerView("detail");
    setEditForm(null);
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
  }

  function openCreate(prefill = {}) {
    setCreateForm(emptyAgentForm(prefill));
    setCreateValidationError(null);
    setCreateSubmit({ status: "idle", error: null });
    setCreatePhotoFile(null);
    setCreatedLoginInfo(null);
    setDrawerView("create");
  }

  function openResetPassword() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setResetForm({ loginIdentifier: detail.loginAccount?.loginIdentifier || "", temporaryPassword: "" });
    setResetValidationError(null);
    setResetSubmit({ status: "idle", error: null });
    setResetCreatedInfo(null);
    setDrawerView("resetPassword");
  }

  function openEdit() {
    const detail = detailState.data;
    if (!detail) {
      return;
    }
    setEditForm({
      displayName: detail.displayName ?? "",
      legalName: detail.legalName ?? "",
      ownerName: detail.ownerName ?? "",
      primaryPhoneNumber: detail.primaryPhoneNumber ?? "",
      lineId: detail.lineId ?? "",
      status: detail.status ?? "Active",
      note: detail.note ?? "",
    });
    setEditValidationError(null);
    setEditSubmit({ status: "idle", error: null });
    setEditPhotoFile(null);
    setDrawerView("edit");
  }

  function closeDrawer() {
    setDrawerView(null);
    setSelectedId(null);
    setCreatedLoginInfo(null);
    setResetCreatedInfo(null);
    if (searchParams.has("createForSuperAgentId") || searchParams.has("createForPlcId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("createForSuperAgentId");
      next.delete("createForPlcId");
      setSearchParams(next, { replace: true });
    }
  }

  // A Super Agent/PLC detail's "next actions" links here with
  // ?createForSuperAgentId=<id>[&createForPlcId=<id>] - open Create
  // prefilled with those parents instead of requiring them to be re-picked.
  useEffect(() => {
    const prefillSuperAgentId = searchParams.get("createForSuperAgentId");
    const prefillPlcId = searchParams.get("createForPlcId");
    if (prefillSuperAgentId && drawerView === null) {
      openCreate({ superAgentOrganizationId: prefillSuperAgentId, plcOrganizationId: prefillPlcId || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only
  }, []);

  function handleCreateSubmit() {
    if (!createForm.superAgentOrganizationId) {
      setCreateValidationError("กรุณาเลือก Super Agent ต้นสังกัด");
      return;
    }
    if (!createForm.plcOrganizationId) {
      setCreateValidationError("กรุณาเลือก PLC ต้นสังกัด");
      return;
    }
    const trimmedName = createForm.displayName.trim();
    if (!trimmedName) {
      setCreateValidationError("กรุณาระบุ displayName");
      return;
    }
    const trimmedLoginIdentifier = createForm.loginIdentifier.trim();
    if (createForm.createLoginAccount) {
      if (!trimmedLoginIdentifier) {
        setCreateValidationError("กรุณาระบุ loginIdentifier สำหรับบัญชีเข้าสู่ระบบ");
        return;
      }
      if (createForm.temporaryPassword.length < 6) {
        setCreateValidationError("temporaryPassword ต้องมีอย่างน้อย 6 ตัวอักษร");
        return;
      }
    }
    setCreateValidationError(null);
    setCreateSubmit({ status: "submitting", error: null });
    setCreatePhotoError(null);
    createAgent({ ...createForm, displayName: trimmedName, loginIdentifier: trimmedLoginIdentifier })
      // P161: a picture can only be attached once the Agent exists, so it rides
      // here rather than in the create body.
      //
      // A picture that fails to upload must NOT fail the create: the Agent is
      // already saved by this point, and reporting the whole thing as failed
      // would send the operator back to re-create a record that is already
      // there. The failure is reported on its own instead, and the picture can
      // be attached again from แก้ไข Agent.
      .then(async (created) => {
        if (!createPhotoFile || !created?.agentId) {
          return true;
        }
        try {
          await uploadAgentPhoto(created.agentId, createPhotoFile);
          return true;
        } catch (photoError) {
          setCreatePhotoError(describeAgentPhotoError(photoError));
          return false;
        }
      })
      .then((photoAttached) => {
        setCreateSubmit({ status: "success", error: null });
        refetchList();
        if (createForm.createLoginAccount) {
          // Show the just-submitted credentials once, then close on "ปิด" - never re-fetched from the server.
          setCreatedLoginInfo({
            agentDisplayName: trimmedName,
            loginIdentifier: trimmedLoginIdentifier,
            temporaryPassword: createForm.temporaryPassword,
          });
        } else if (photoAttached) {
          closeDrawer();
        }
        // When the picture failed, the drawer deliberately stays open so the
        // message about it is actually read rather than closing over it.
      })
      .catch((error) => {
        setCreateSubmit({ status: "error", error });
      });
  }

  function handleResetSubmit() {
    const trimmedLoginIdentifier = resetForm.loginIdentifier.trim();
    if (!trimmedLoginIdentifier) {
      setResetValidationError("กรุณาระบุ loginIdentifier");
      return;
    }
    if (resetForm.temporaryPassword.length < 6) {
      setResetValidationError("temporaryPassword ต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }
    setResetValidationError(null);
    setResetSubmit({ status: "submitting", error: null });
    createOrResetAgentLoginAccount(selectedId, { loginIdentifier: trimmedLoginIdentifier, temporaryPassword: resetForm.temporaryPassword })
      .then(() => {
        setResetSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        setResetCreatedInfo({
          agentDisplayName: detailState.data?.displayName ?? "",
          loginIdentifier: trimmedLoginIdentifier,
          temporaryPassword: resetForm.temporaryPassword,
          wasReset: Boolean(detailState.data?.loginAccount?.hasLoginAccount),
        });
      })
      .catch((error) => {
        setResetSubmit({ status: "error", error });
      });
  }

  function handleEditSubmit() {
    if (!editForm) {
      return;
    }
    const trimmedName = editForm.displayName.trim();
    if (!trimmedName) {
      setEditValidationError("displayName ไม่สามารถเว้นว่างได้");
      return;
    }
    setEditValidationError(null);
    setEditPhotoError(null);
    setEditSubmit({ status: "submitting", error: null });
    updateAgent(selectedId, { ...editForm, displayName: trimmedName })
      // P161: the picture is a SEPARATE write, made only when a file was
      // actually picked - saving an unrelated edit therefore can never blank or
      // replace the stored picture. Same honesty rule as create: the details
      // are already saved by now, so a failed picture is reported on its own
      // rather than presented as the edit having failed.
      .then(async () => {
        if (!editPhotoFile) {
          return true;
        }
        try {
          await uploadAgentPhoto(selectedId, editPhotoFile);
          return true;
        } catch (photoError) {
          setEditPhotoError(describeAgentPhotoError(photoError));
          return false;
        }
      })
      .then((photoAttached) => {
        setEditSubmit({ status: "success", error: null });
        setDetailReloadToken((token) => token + 1);
        refetchList();
        if (photoAttached) {
          setDrawerView("detail");
        }
        // A failed picture keeps the form open, with the message beside the
        // field that caused it.
      })
      .catch((error) => {
        setEditSubmit({ status: "error", error });
      });
  }

  const items = listData?.items ?? [];
  const totalCount = listData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / AGENTS_DEFAULT_PAGE_SIZE));
  const hasActiveFilters = Boolean(search || statusFilter || superAgentFilter || plcFilter);

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-wl-page">
        <header className="wx-wl-head">
          <div>
            <h1>Agents</h1>
            <p>จัดการ Agent ทุกคนใต้ PLC/Super Agent ทุกสายงาน สร้างและแก้ไขข้อมูลได้จากหน้านี้</p>
          </div>
          {primaryRole && (
            <span className="wx-wl-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-wl-toolbar">
          <label className="wx-wl-search">
            <span className="wx-visually-hidden">Search agent code or name</span>
            <input type="search" placeholder="ค้นหารหัส Agent หรือชื่อ Agent..." value={searchInput} onChange={(event) => setSearchInput(event.target.value)} />
          </label>

          <div className="wx-wl-toolbar-actions">
            <button type="button" className="wx-button wx-button--primary wx-wl-create-button" onClick={() => openCreate()}>
              + Create Agent
            </button>
          </div>

          <div className="wx-wl-filters">
            <label className="wx-wl-filter">
              <span>Super Agent</span>
              <select value={superAgentFilter} onChange={(event) => setSuperAgentFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {superAgentOptions.map((option) => (
                  <option key={option.superAgentOrganizationId} value={option.superAgentOrganizationId}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="wx-wl-filter">
              <span>PLC / บมจ</span>
              <select value={plcFilter} disabled={!superAgentFilter} onChange={(event) => setPlcFilter(event.target.value)}>
                <option value="">{superAgentFilter ? "ทั้งหมด" : "เลือก Super Agent ก่อน"}</option>
                {filterPlcOptions.items.map((option) => (
                  <option key={option.plcOrganizationId} value={option.plcOrganizationId}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label className="wx-wl-filter">
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">ทั้งหมด</option>
                {WORKER_LINE_STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <section className="wx-panel wx-wl-list-panel">
          <div className="wx-wl-list-head">
            <h2 className="wx-panel-title">รายการ Agent</h2>
            <span className="wx-wl-list-count">
              {totalCount} รายการ{listStatus === "loading" && listData !== null ? " · กำลังโหลด..." : ""}
            </span>
          </div>

          {listStatus === "error" && listData !== null && (
            <p className="wx-wl-inline-error" role="alert">
              {describeListError(listError)}
            </p>
          )}

          {listStatus === "error" && listData === null ? (
            <div className="wx-state-card wx-state-card--danger" role="alert">
              <p className="wx-state-body">{describeListError(listError)}</p>
            </div>
          ) : listStatus === "loading" && listData === null ? (
            <PageSkeleton variant="table" label="กำลังโหลดรายการ Agent..." />
          ) : items.length === 0 ? (
            <p className="wx-empty-list">{hasActiveFilters ? "ไม่พบผลลัพธ์ที่ตรงกับตัวกรองหรือคำค้นหา" : "ยังไม่มี Agent ในระบบ"}</p>
          ) : (
            <>
              <div className="wx-wl-table-wrap">
                <table className="wx-wl-table">
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Super Agent</th>
                      <th>PLC</th>
                      <th>ผู้ดูแล</th>
                      <th>phone / lineId</th>
                      <th>Status</th>
                      <th>คำเชิญ</th>
                      <th>ใบสมัคร</th>
                      {/* P156: one column, two meanings. A job-side Agent's
                          line is employers, so the cell says so per row rather
                          than reporting every job-side Agent as "0 แรงงาน". */}
                      <th>แรงงาน / นายจ้าง</th>
                      <th>Active Assign.</th>
                      <th aria-hidden="true">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.agentId} className="wx-wl-row" onClick={() => openDetail(item.agentId)}>
                        <td>
                          <strong className="wx-wl-cell-strong">{item.displayName}</strong>
                          <span className="wx-wl-code">{item.agentCode}</span>
                        </td>
                        <td>{item.superAgentName || "-"}</td>
                        <td>{item.plcName || "-"}</td>
                        <td>{item.ownerName || "-"}</td>
                        <td>
                          {item.primaryPhoneNumber || "-"}
                          {item.lineId ? ` / ${item.lineId}` : ""}
                        </td>
                        <td>
                          <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                        </td>
                        <td>{item.inviteCount}</td>
                        <td>{item.applicationCount}</td>
                        <td>
                          {isJobDemandSide(item.organizationSide)
                            ? `${item.employerCount ?? 0} นายจ้าง`
                            : item.workerCount}
                        </td>
                        <td>{item.activeAssignmentCount}</td>
                        <td>
                          <div className="wx-wl-row-actions">
                            <button
                              type="button"
                              className="wx-wl-row-action"
                              onClick={(event) => {
                                event.stopPropagation();
                                openDetail(item.agentId);
                              }}
                            >
                              เปิดรายละเอียด
                            </button>
                            {/* Owner: "มาที่ชื่อ agent นั้นแล้วคัดลอก link แล้วส่งเลย".
                                Sits on the row itself, not behind the drawer, because
                                the whole point is that sending an invite for an Agent
                                should not require opening anything first.

                                Which link depends on which side the Agent's line is
                                on, because they recruit different people: a job-side
                                Agent invites EMPLOYERS and has no worker pipeline at
                                all. Showing the worker link there would be a button
                                that can only ever error. */}
                            {canIssueInviteLink ? (
                              <CopyInviteLinkButton
                                agentId={item.agentId}
                                agentDisplayName={item.displayName}
                                variant={isJobDemandSide(item.organizationSide) ? "employer" : "worker"}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-wl-cards">
                {items.map((item) => (
                  <div className="wx-wl-card" key={item.agentId} onClick={() => openDetail(item.agentId)}>
                    <div className="wx-wl-card-top">
                      <div>
                        <strong>{item.displayName}</strong>
                        <span className="wx-wl-card-sub">
                          {item.agentCode} · {item.plcName || "-"}
                        </span>
                      </div>
                      <span className={`wx-badge wx-badge--${statusBadgeVariant(item.status)}`}>{item.status}</span>
                    </div>
                    <div className="wx-wl-card-grid">
                      <div>
                        <label>คำเชิญ</label>
                        <strong>{item.inviteCount}</strong>
                      </div>
                      <div>
                        <label>ใบสมัคร</label>
                        <strong>{item.applicationCount}</strong>
                      </div>
                      <div>
                        <label>{agentLineNoun(item.organizationSide)}</label>
                        <strong>
                          {isJobDemandSide(item.organizationSide) ? (item.employerCount ?? 0) : item.workerCount}
                        </strong>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="wx-wl-row-action wx-wl-row-action--full"
                      onClick={(event) => {
                        event.stopPropagation();
                        openDetail(item.agentId);
                      }}
                    >
                      เปิดรายละเอียด
                    </button>
                  </div>
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>

      <DetailDrawer
        open={drawerView !== null}
        onClose={closeDrawer}
        labelledBy={DRAWER_TITLE_ID}
        selectableSelector="tr.wx-wl-row, .wx-wl-card, .wx-wl-create-button"
      >
        {drawerView === "detail" && (
          <AgentDetailView
            titleId={DRAWER_TITLE_ID}
            state={detailState}
            onEdit={openEdit}
            onManageLoginAccount={openResetPassword}
            canIssueInviteLink={canIssueInviteLink}
          />
        )}
        {drawerView === "create" && createdLoginInfo && (
          <AgentLoginCreatedView titleId={DRAWER_TITLE_ID} wasReset={false} onDone={closeDrawer} {...createdLoginInfo} />
        )}
        {drawerView === "create" && !createdLoginInfo && (
          <AgentCreateView
            titleId={DRAWER_TITLE_ID}
            form={createForm}
            onChange={(patch) => setCreateForm((previous) => ({ ...previous, ...patch }))}
            superAgentOptions={superAgentOptions}
            plcOptions={createPlcOptions.items}
            plcOptionsStatus={createPlcOptions.status}
            validationError={createValidationError ?? createPhotoError}
            submitState={createSubmit}
            onSubmit={handleCreateSubmit}
            photoFile={createPhotoFile}
            onPhotoFileChange={(file) => {
              setCreatePhotoFile(file);
              setCreatePhotoError(null);
            }}
          />
        )}
        {drawerView === "resetPassword" && resetCreatedInfo && (
          <AgentLoginCreatedView titleId={DRAWER_TITLE_ID} onDone={() => setDrawerView("detail")} {...resetCreatedInfo} />
        )}
        {drawerView === "resetPassword" && !resetCreatedInfo && (
          <AgentLoginAccountView
            titleId={DRAWER_TITLE_ID}
            agentDisplayName={detailState.data?.displayName ?? ""}
            hasLoginAccount={Boolean(detailState.data?.loginAccount?.hasLoginAccount)}
            form={resetForm}
            onChange={(patch) => setResetForm((previous) => ({ ...previous, ...patch }))}
            validationError={resetValidationError}
            submitState={resetSubmit}
            onSubmit={handleResetSubmit}
            onBack={() => setDrawerView("detail")}
          />
        )}
        {drawerView === "edit" && editForm && (
          <AgentEditView
            titleId={DRAWER_TITLE_ID}
            code={detailState.data?.agentCode}
            form={editForm}
            onChange={(patch) => setEditForm((previous) => ({ ...previous, ...patch }))}
            validationError={editValidationError ?? editPhotoError}
            submitState={editSubmit}
            onSubmit={handleEditSubmit}
            onBack={() => setDrawerView("detail")}
            agentId={selectedId}
            hasStoredPhoto={Boolean(detailState.data?.photo)}
            photoFile={editPhotoFile}
            onPhotoFileChange={(file) => {
              setEditPhotoFile(file);
              setEditPhotoError(null);
            }}
          />
        )}
      </DetailDrawer>
    </AppShell>
  );
}
